/**
 * The status bar's height: the one top-inset rule every phone overlay and bar reads (I-1163).
 *
 * The app's web view runs under the status bar and the shell sends its height as the `insets` top
 * (`--shell-inset-top`, set by `ShellRuntime`); mobile Safari reports it as
 * `env(safe-area-inset-top)`. One of the two is 0 wherever the page runs, so their sum is the
 * status bar either way, and 0 on a desktop. An overlay laid out from the screen's top edge without
 * it drew its first row under the clock, where a tap never reaches the page (I-1161's create
 * sheet; a tall dialog, the All work areas item panel and a view's Customize panel, I-1163).
 *
 * CSS reads `STATUS_BAR_INSET`; code that sizes in px reads `statusBarInsetPx()`, which measures
 * the same expression. `statusBarInset.guard.test.ts` holds every other `.ts`/`.tsx` file to these.
 */
export const STATUS_BAR_INSET = 'calc(env(safe-area-inset-top) + var(--shell-inset-top, 0px))';

/**
 * The home indicator's height, or whatever the native bars still cover at the bottom: the one
 * bottom-inset rule an overlay reads, the top rule's other edge (I-1209). Mobile Safari reports the
 * home indicator as `env(safe-area-inset-bottom)`; the app sends what its tab bar covers as the
 * `insets` bottom (`--shell-inset-bottom`), and the web view's own safe area carries the home
 * indicator. Both can be set at once — the tab bar stands over the home indicator — so this is
 * their larger, never their sum. A tall dialog laid out to the screen's bottom edge put its last
 * action (Cancel) under the home indicator (qa-472a: its bottom at 530 in a 520 view, inset 34).
 *
 * Not for a layer that moves with the keyboard: with the keyboard up nothing rests under it.
 */
export const HOME_INDICATOR_INSET =
  'max(env(safe-area-inset-bottom), var(--shell-inset-bottom, 0px))';

/**
 * The home indicator alone: what rests under a layer that covers the tab bar (a phone `Sheet`, which
 * fades the app's tab bar out, or mobile Safari's own bottom bar). The app keeps reporting the tab
 * bar's place as `--shell-inset-bottom` while a sheet hides the bar, so the page under the sheet
 * does not reflow, and the web view's `env(safe-area-inset-bottom)` takes the tab bar in as well;
 * so the shell measures the home indicator on its own and sends it as `insets.homeIndicator`
 * (`--shell-home-indicator`, set by `ShellRuntime`). Mobile Safari, and an app that predates it,
 * read `env(safe-area-inset-bottom)`. A sheet read `HOME_INDICATOR_INSET` and its last action sat
 * 49 above the home indicator, the faded tab bar's height (I-1260).
 */
export const HOME_INDICATOR_ALONE = 'var(--shell-home-indicator, env(safe-area-inset-bottom))';

/** `STATUS_BAR_INSET` in px, measured now. */
export function statusBarInsetPx(): number {
  if (typeof document === 'undefined' || !document.body) return 0;
  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;padding-top:${STATUS_BAR_INSET}`;
  document.body.appendChild(probe);
  const value = Number.parseFloat(getComputedStyle(probe).paddingTop);
  probe.remove();
  return Number.isFinite(value) ? value : 0;
}
